import {
    collection,
    addDoc,
    getDocs,
    query,
    where,
    serverTimestamp,
} from 'firebase/firestore';
import { db, isFirebaseConfigured } from './config';

const MAIL_COLLECTION = 'mail';

function safeName(customerName) {
    return (customerName || '').trim() || 'Customer';
}

function formatCurrency(value) {
    const amount = Number(value || 0);
    return `PHP ${amount.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatPromoDate(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-PH', { month: 'long', day: 'numeric', year: 'numeric' });
}

function buildOrderEmail(templateId, data) {
    const name       = safeName(data.customerName);
    const total      = formatCurrency(data.orderTotal);
    const orderId    = data.orderId || '';
    const expiryNote = 'Please complete checkout within 72 hours to keep your order active.';

    switch (templateId) {
        case 'order-received':
            return {
                subject: `Order Received - ${orderId}`,
                text: `Hi ${name},\n\nWe received your order (${orderId}) and it is now pending approval.\n\nOrder total: ${total}\n\nWe will email you once your order is approved.\n\nThank you,\nJG Printing`,
                html: `<p>Hi ${name},</p><p>We received your order (<strong>${orderId}</strong>) and it is now pending approval.</p><p><strong>Order total:</strong> ${total}</p><p>We will email you once your order is approved.</p><p>Thank you,<br>JG Printing</p>`,
            };
        case 'order-approved':
            return {
                subject: `Order ${orderId} Approved - Proceed to Checkout`,
                text: `Hi ${name},\n\nGood news! Your order (${orderId}) has been approved.\n\nOrder total: ${total}\n\n${expiryNote}\n\nThank you,\nJG Printing`,
                html: `<p>Hi ${name},</p><p>Good news! Your order (<strong>${orderId}</strong>) has been approved.</p><p><strong>Order total:</strong> ${total}</p><p>${expiryNote}</p><p>Thank you,<br>JG Printing</p>`,
            };
        case 'order-in-production':
            return {
                subject: `Order ${orderId} Is Now In Production`,
                text: `Hi ${name},\n\nYour order (${orderId}) is now in production.\n\nOrder total: ${total}\n\nThank you,\nJG Printing`,
                html: `<p>Hi ${name},</p><p>Your order (<strong>${orderId}</strong>) is now in production.</p><p><strong>Order total:</strong> ${total}</p><p>Thank you,<br>JG Printing</p>`,
            };
        case 'order-ready-pickup':
            return {
                subject: `Order ${orderId} Is Ready for Pickup`,
                text: `Hi ${name},\n\nYour order (${orderId}) is ready for pickup or delivery.\n\nOrder total: ${total}\n\nThank you,\nJG Printing`,
                html: `<p>Hi ${name},</p><p>Your order (<strong>${orderId}</strong>) is ready for pickup or delivery.</p><p><strong>Order total:</strong> ${total}</p><p>Thank you,<br>JG Printing</p>`,
            };
        case 'order-delivered':
            return {
                subject: `Order ${orderId} Delivered - Thank You`,
                text: `Hi ${name},\n\nYour order (${orderId}) has been delivered.\n\nOrder total: ${total}\n\nThank you,\nJG Printing`,
                html: `<p>Hi ${name},</p><p>Your order (<strong>${orderId}</strong>) has been delivered.</p><p><strong>Order total:</strong> ${total}</p><p>Thank you,<br>JG Printing</p>`,
            };
        case 'order-cancelled': {
            const reason = data?.extra?.cancellationReason ? `Reason: ${data.extra.cancellationReason}\n\n` : '';
            return {
                subject: `Order ${orderId} Has Been Cancelled`,
                text: `Hi ${name},\n\nYour order (${orderId}) has been cancelled.\n\n${reason}If you have questions, please reply to this email.\n\nThank you,\nJG Printing`,
                html: `<p>Hi ${name},</p><p>Your order (<strong>${orderId}</strong>) has been cancelled.</p>${reason ? `<p>${reason.replace(/\n/g, '<br>')}</p>` : ''}<p>If you have questions, please reply to this email.</p><p>Thank you,<br>JG Printing</p>`,
            };
        }
        default:
            return {
                subject: data.subject || 'Notification from JG Printing',
                text: data.text || '',
                html: data.html || '',
            };
    }
}

export async function queueEmail({ to, subject, text, html, extra = {} }) {
    if (!isFirebaseConfigured || !db || !to || !subject) {
        return { queued: false, error: 'Missing configuration or email parameters.' };
    }
    try {
        await addDoc(collection(db, MAIL_COLLECTION), {
            to,
            message: { subject, text: text || '', html: html || '' },
            ...extra,
            createdAt: serverTimestamp(),
        });
        return { queued: true, error: null };
    } catch (error) {
        console.error('Failed to queue email:', error);
        return { queued: false, error: error.message };
    }
}

export async function queueOrderEmail({ to, customerName, subject, templateId, orderId, status, orderTotal, extra = {} }) {
    if (!to) return { queued: false, error: 'Missing recipient email.' };
    const template     = buildOrderEmail(templateId, { customerName, orderId, status, orderTotal, extra });
    const finalSubject = subject || template.subject;
    return queueEmail({
        to,
        subject: finalSubject,
        text:    template.text,
        html:    template.html,
        extra:   { templateId, orderId, status, orderTotal, ...extra },
    });
}

// ─── Admin email helpers ──────────────────────────────────────────────────────

/**
 * Fetch all admin emails from the 'admins' Firestore collection.
 * Each admin document is expected to have an 'email' field.
 * Returns a deduplicated array of email strings.
 */
export async function getAdminEmails() {
    if (!isFirebaseConfigured || !db) return [];
    try {
        const snapshot = await getDocs(collection(db, 'admins'));
        const emails   = [];
        snapshot.forEach(docSnap => {
            const email = (docSnap.data()?.email || '').trim().toLowerCase();
            if (email && !emails.includes(email)) {
                emails.push(email);
            }
        });
        return emails;
    } catch (err) {
        console.error('getAdminEmails: failed to fetch admin emails:', err);
        return [];
    }
}

/**
 * Send a low stock alert email to all admins.
 *
 * @param {string}   productName   - Name of the product with low stock
 * @param {string}   subcategory   - Subcategory (T-Shirt, Polo Shirt, Jersey)
 * @param {Array}    lowStockSizes - Array of { size, stock } objects for sizes that are low/out
 * @param {string}   trigger       - 'manual' (admin saved inventory) | 'order' (order went In Production)
 * @param {string}   orderId       - Optional: order ID that triggered this (if trigger === 'order')
 */
export async function sendLowStockAlertEmail({ productName, subcategory, lowStockSizes, trigger = 'manual', orderId = null }) {
    if (!productName || !lowStockSizes || lowStockSizes.length === 0) return;

    const adminEmails = await getAdminEmails();
    if (adminEmails.length === 0) {
        console.warn('sendLowStockAlertEmail: no admin emails found, skipping alert.');
        return;
    }

    const triggerNote = trigger === 'order' && orderId
        ? `This alert was triggered when order <strong>${orderId}</strong> moved to In Production and stock was deducted.`
        : 'This alert was triggered when inventory was manually updated in Manage Inventory.';

    const sizeRows = lowStockSizes.map(({ size, stock }) => {
        const status = stock === 0 ? 'Out of Stock' : `Low Stock (${stock} remaining)`;
        const color  = stock === 0 ? '#b71c1c' : '#b07c00';
        return `<tr>
            <td style="padding:6px 12px; border-bottom:1px solid #eee;">${size}</td>
            <td style="padding:6px 12px; border-bottom:1px solid #eee; color:${color}; font-weight:600;">${status}</td>
        </tr>`;
    }).join('');

    const sizeText = lowStockSizes.map(({ size, stock }) =>
        stock === 0
            ? `  - ${size}: Out of Stock`
            : `  - ${size}: Low Stock (${stock} remaining)`
    ).join('\n');

    const subject = `⚠️ Low Stock Alert — ${productName}`;

    const html = `
        <div style="font-family:sans-serif; max-width:600px; margin:0 auto; color:#333;">
            <div style="background:#22314a; padding:20px 24px; border-radius:8px 8px 0 0;">
                <h2 style="color:#fff; margin:0; font-size:18px;">⚠️ Low Stock Alert</h2>
            </div>
            <div style="background:#fff; padding:24px; border:1px solid #e5e7eb; border-radius:0 0 8px 8px;">
                <p>Hello,</p>
                <p>
                    The following product has reached low or out-of-stock levels and may need to be restocked:
                </p>
                <table style="width:100%; border-collapse:collapse; margin:16px 0; background:#f9fafb; border-radius:6px; overflow:hidden;">
                    <tr style="background:#f3f4f6;">
                        <th style="padding:8px 12px; text-align:left; font-size:13px; color:#555;">Product</th>
                        <td style="padding:8px 12px; font-weight:600;">${productName}</td>
                    </tr>
                    <tr>
                        <th style="padding:8px 12px; text-align:left; font-size:13px; color:#555;">Subcategory</th>
                        <td style="padding:8px 12px;">${subcategory || '—'}</td>
                    </tr>
                </table>
                <p style="font-weight:600; margin-bottom:8px;">Affected Sizes:</p>
                <table style="width:100%; border-collapse:collapse; border:1px solid #e5e7eb; border-radius:6px; overflow:hidden;">
                    <thead>
                        <tr style="background:#f3f4f6;">
                            <th style="padding:8px 12px; text-align:left; font-size:13px;">Size</th>
                            <th style="padding:8px 12px; text-align:left; font-size:13px;">Status</th>
                        </tr>
                    </thead>
                    <tbody>${sizeRows}</tbody>
                </table>
                <p style="margin-top:16px; font-size:13px; color:#666;">${triggerNote}</p>
                <p style="margin-top:16px;">
                    Please update the inventory in the
                    <a href="localhost:3000/admin/products/inventory" style="color:#22314a; font-weight:600;">Admin Manage Inventory</a>
                    page.
                </p>
                <p style="color:#999; font-size:12px; margin-top:24px;">
                    This is an automated alert from JG Printing's inventory management system.
                </p>
            </div>
        </div>
    `;

    const text = `Low Stock Alert — ${productName}\n\nSubcategory: ${subcategory || '—'}\n\nAffected Sizes:\n${sizeText}\n\n${
        trigger === 'order' && orderId
            ? `Triggered by order ${orderId} moving to In Production.`
            : 'Triggered by manual inventory update.'
    }\n\nPlease update inventory at: localhost:3000/admin/products/inventory\n\nThis is an automated alert from JG Printing.`;

    // Send to all admins — queue each individually so one failure doesn't block others
    const tasks = adminEmails.map(email =>
        queueEmail({ to: email, subject, text, html })
    );

    try {
        await Promise.all(tasks);
        console.log(`sendLowStockAlertEmail: ✓ Alert sent to ${adminEmails.length} admin(s) for "${productName}"`);
    } catch (err) {
        console.error('sendLowStockAlertEmail: failed to send some alerts:', err);
    }
}

// ─── Promotion email helpers ──────────────────────────────────────────────────

function buildPromotionEmail(promotion, user) {
    const name               = safeName(`${user?.firstName || ''} ${user?.lastName || ''}`.trim());
    const promoName          = promotion?.promotionName  || 'New Promotion';
    const promoCode          = promotion?.promoCode      || '';
    const discountValue      = promotion?.discountValue  || '';
    const promotionType      = promotion?.promotionType  || '';
    const discountedProducts = promotion?.discountedProducts || 'selected items';
    const startDate          = formatPromoDate(promotion?.startDate);
    const endDate            = formatPromoDate(promotion?.endDate);

    const dateLine     = startDate && endDate ? `${startDate} to ${endDate}` : '';
    const codeLine     = promoCode ? `Promo code: ${promoCode}` : '';
    const discountLine = discountValue
        ? `Discount: ${discountValue}${promotionType === 'percentage' ? '%' : ''}`
        : '';

    const lines = [
        `Hi ${name},`,
        '',
        `We have a new promotion for you: ${promoName}.`,
        discountLine,
        `Applies to: ${discountedProducts}`,
        dateLine,
        codeLine,
        '',
        'You are receiving this email because you opted in to promotional updates.',
        'You can update your preferences in your account settings at any time.',
        '',
        'Thank you,',
        'JG Printing',
    ].filter(Boolean);

    const text = lines.join('\n');

    const htmlParts = [
        `<p>Hi ${name},</p>`,
        `<p>We have a new promotion for you: <strong>${promoName}</strong>.</p>`,
        discountLine ? `<p><strong>${discountLine}</strong></p>` : '',
        `<p><strong>Applies to:</strong> ${discountedProducts}</p>`,
        dateLine ? `<p><strong>Dates:</strong> ${dateLine}</p>` : '',
        codeLine ? `<p><strong>${codeLine}</strong></p>` : '',
        '<p>You are receiving this email because you opted in to promotional updates. You can update your preferences in your account settings at any time.</p>',
        '<p>Thank you,<br>JG Printing</p>',
    ].filter(Boolean);

    return {
        subject: `New Promotion: ${promoName}`,
        text,
        html: htmlParts.join(''),
    };
}

async function getPromotionRecipients() {
    if (!isFirebaseConfigured || !db) return [];

    const recipients = new Map();
    const usersRef   = collection(db, 'users');

    const promoQuery = query(usersRef, where('promotionalEmails', '==', true));
    const agreeQuery = query(usersRef, where('agreeToPromotions', '==', true));

    const [promoSnap, agreeSnap] = await Promise.all([getDocs(promoQuery), getDocs(agreeQuery)]);

    [promoSnap, agreeSnap].forEach((snap) => {
        snap.forEach((docSnap) => {
            const data  = docSnap.data() || {};
            const email = (data.email || '').trim().toLowerCase();
            if (!email) return;
            if (!recipients.has(email)) {
                recipients.set(email, { id: docSnap.id, ...data });
            }
        });
    });

    return Array.from(recipients.values());
}

export async function sendPromotionBroadcast(promotion) {
    try {
        const recipients = await getPromotionRecipients();
        if (!recipients.length) return { sent: 0, skipped: 0, error: null };

        const sendTasks = recipients.map((user) => {
            const { subject, text, html } = buildPromotionEmail(promotion, user);
            return queueEmail({
                to: user.email, subject, text, html,
                extra: { promotionId: promotion?.id || null },
            });
        });

        const results = await Promise.all(sendTasks);
        const sent    = results.filter((r) => r?.queued).length;
        const skipped = results.length - sent;
        return { sent, skipped, error: null };
    } catch (error) {
        console.error('Failed to send promotion broadcast:', error);
        return { sent: 0, skipped: 0, error: error.message };
    }
}